import React, { ReactElement } from "react";
import styled, { css } from "styled-components";

import { Fonts } from "assets/fonts";

import { TabProps } from "./Tab";

const TabsWrapper = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TabList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  background-color: transparent;
  border-bottom: 1px solid ${(p) => p.theme.borderDefault};
  list-style: none;
  margin: 0;
  padding: 0;

  li + li {
    margin-left: 16px;
  }
`;

const activeTabStyles = css`
  border-bottom: 2px solid ${(p) => p.theme.primaryHighContrast};
  box-shadow: 0 1px 0 0 ${(p) => p.theme.primaryHighContrast};
  color: ${(p) => p.theme.textHighestContrast};
  font-weight: 700;
  z-index: 1;
`;
const TabButton = styled.button<{ $isActive: boolean; $isNextActive: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  ${Fonts.semiBold_14};
  color: ${(p) => p.theme.textMediumContrast};
  border: none;
  border-bottom: 2px solid transparent;
  background-color: transparent;
  transition:
    color 0.2s ease,
    border 0.2s ease;
  cursor: pointer;

  &:focus {
    outline: 1px dotted ${(p) => p.theme.textMediumContrast};
  }

  ${(p) => p.$isActive && activeTabStyles}
`;

const TabContent = styled.div`
  flex: 1;
  padding-top: 24px;
  ${Fonts.regular_14};
`;

const TabPanel = styled.div``;

interface TabsProps<T> {
  activeTab: T;
  children: React.ReactElement<TabProps<T>>[];
  className?: string;
  onTabClick: (tabKey: T) => void;
}
export const Tabs = <T extends string>(props: TabsProps<T>) => {
  const { activeTab, children: tabs, className, onTabClick } = props;
  const cn = className ? `${className}-` : "";
  return (
    <TabsWrapper className={className}>
      <TabList role="tablist" className={`${cn}tabs`}>
        {React.Children.map(tabs, (tab: ReactElement<TabProps<T>>, idx) => {
          const { tabKey, renderTitle } = tab.props;
          const isActive = tabKey === activeTab;
          return (
            <li key={`${tabKey}-tab-btn`} role="presentation">
              <TabButton
                id={`${tabKey}-tab-btn`}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onTabClick(tabKey)}
                $isActive={isActive}
                $isNextActive={tabs[idx + 1]?.props.tabKey === activeTab}
              >
                {renderTitle(isActive)}
              </TabButton>
            </li>
          );
        })}
      </TabList>
      <TabContent>
        {tabs
          .filter((tab) => tab.props.tabKey === activeTab)
          .map(({ props: { tabKey, children } }) => (
            <TabPanel
              key={`${tabKey}-tab-content`}
              role="tabpanel"
              aria-labelledby={`${tabKey}-tab-btn`}
              className={`${cn}content`}
            >
              {children}
            </TabPanel>
          ))}
      </TabContent>
    </TabsWrapper>
  );
};
